import { BasicQueryType, MutationBasicaParam } from "@/Utils/RequisicaoUtilitario";
import { Axios, CustomAxiosRequestConfig } from "../../Axios";
import { AxiosResponse } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Recibo } from "@/Models/Financeiro/Titulo/Recibo";

type NovoCompartilhamento = {
    dataDeExpiracao?: Date
}

export class DocumentoService {
    public static obterRecursoCompartilhado(guid: string) {
        return Axios.get<Blob>(`quiron/documentos/compartilhado/${guid}`, {
            responseType: "blob",
        });
    }

    public static desativarRecursoCompartilhado(guid: string) {
        return Axios.delete<void>(`quiron/documentos/compartilhado/${guid}`);
    }

    public static URLDoArquivo(nome: string) {
        const prefix = Axios.defaults.baseURL ?? "/";
        return new URL(`${prefix}/quiron/documentos/arquivo/${nome}`).toString();
    }

    public static obterArquivo(nome: string) {
        return Axios.get<Blob>(`quiron/documentos/arquivo/${nome}`, {
            responseType: "blob",
        });
    }

    public static compartilharArquivo(nomeGUID: string, opcoes: NovoCompartilhamento) {
        return Axios.post<void>(`quiron/documentos/arquivo/${nomeGUID}`, opcoes);
    }

    public static listarGUIDsDoArquivo(nomeGUID: string) {
        return Axios.get<string[]>(`quiron/documentos/arquivo/${nomeGUID}/ids`);
    }

    public static obterHTMLDaFichaDeSaudeDoAluno(codigoDoAluno: number, config?: CustomAxiosRequestConfig) {
        return Axios.get<string>(`quiron/documentos/saude/ficha/aluno/${codigoDoAluno}`, { ...config });
    }
    public static obterHTMLDaFichaDeSaudeDoAlunoPdf(codigoDoAluno: number, config?: CustomAxiosRequestConfig) {
        return Axios.get<Blob>(`quiron/documentos/saude/ficha/aluno/${codigoDoAluno}/pdf`, { responseType: "blob", ...config });
    }
    public static obterHTMLDaFichaDeSaudeDoAlunoZip(codViagem: number, config?: CustomAxiosRequestConfig) {
        return Axios.post<Blob>(`quiron/documentos/saude/ficha/viagem/${codViagem}/zip`, undefined, { responseType: "blob", ...config });
    }

    public static compartilharFichaDeSaudeDoAluno(codigoDoAluno: number, opcoes: NovoCompartilhamento) {
        return Axios.post<void>(`quiron/documentos/saude/ficha/aluno/${codigoDoAluno}`, opcoes);
    }

    public static listarGUIDsDaFichaDeSaudeDoAluno(codigoDoAluno: number, config?: CustomAxiosRequestConfig) {
        return Axios.get<string[]>(`quiron/documentos/saude/ficha/aluno/${codigoDoAluno}/ids`, { ...config });
    }

    public static obterHTMLDaFichaDeAutorizacaoDoAluno(
        params: { codigoDoAluno: number; codigoDaViagem: number; codigoDoResponsavel?: number },
        config?: CustomAxiosRequestConfig
    ) {
        const { codigoDoAluno, codigoDaViagem, codigoDoResponsavel } = params;

        return Axios.get<string>(`quiron/documentos/viagem/responsavel/ficha-autorizacao-menores/${codigoDoAluno}`, {
            ...config,
            params: {
                codigoDaViagem,
                codigoDoResponsavel,
                ...config?.params,
            },
        });
    }

    public static obterHTMLDoRecibo(codigoDoTitulo: number, config?: CustomAxiosRequestConfig) {
        return Axios.get<string>(`quiron/documentos/financeiro/titulo/${codigoDoTitulo}/recibo`, { ...config });
    }

    public static atualizarRecibo(codigoDoTitulo: number, payload: Recibo, config?: CustomAxiosRequestConfig) {
        return Axios.put<string>(`quiron/documentos/financeiro/titulo/${codigoDoTitulo}/recibo`, payload, { ...config });
    }
}

export function useObterHTMLDaFichaDeSaude(
    config: BasicQueryType<AxiosResponse<string>>,
    params: Parameters<typeof DocumentoService.obterHTMLDaFichaDeSaudeDoAluno>
){
    const query = useQuery({
        queryKey: ["Documentos", "Documentos.Saude.Ficha.Aluno", params],
        queryFn: async () => DocumentoService.obterHTMLDaFichaDeSaudeDoAluno(...params),
        ...config,
    });

    return query;
}

export function useObterFichaDeSaudeAlunoPdf(
    {
        ...rest
    }:MutationBasicaParam<Parameters<typeof DocumentoService.obterHTMLDaFichaDeSaudeDoAlunoPdf>, AxiosResponse<Blob>>
){
    const mutacao = useMutation({
        ...rest,
        mutationFn: (params) => DocumentoService.obterHTMLDaFichaDeSaudeDoAlunoPdf(...params),
    });

    return mutacao;
}

export function useObterFichaDeSaudeAlunoZip(
    {
        ...rest
    }:MutationBasicaParam<Parameters<typeof DocumentoService.obterHTMLDaFichaDeSaudeDoAlunoZip>, AxiosResponse<Blob>>
){
    const mutacao = useMutation({
        ...rest,
        mutationFn: (params) => DocumentoService.obterHTMLDaFichaDeSaudeDoAlunoZip(...params),
    });

    return mutacao;
}

export function useObterHTMLDaFichaDeAutorizacaoDoAluno(
    config: BasicQueryType<AxiosResponse<string>>,
    params: Parameters<typeof DocumentoService.obterHTMLDaFichaDeAutorizacaoDoAluno>
){

    const query = useQuery({
        queryKey: ["Documentos", "Documentos.Viagem.Responsavel.FichaDeAutorizacao", params],
        queryFn: async () => DocumentoService.obterHTMLDaFichaDeAutorizacaoDoAluno(...params),
        ...config,
    });

    return query;
}

export function useObterHTMLDoRecibo(
    config: BasicQueryType<AxiosResponse<string>>,
    params: Parameters<typeof DocumentoService.obterHTMLDoRecibo>
){

    const query = useQuery({
        queryKey: ["Documentos", "Documentos.Financeiro.Titulo.Recibo", params],
        queryFn: async () => DocumentoService.obterHTMLDoRecibo(...params),
        ...config,
    });

    return query;
}